import { Button, Typography, message } from 'antd';
import { CopyOutlined, CalculatorOutlined } from '@ant-design/icons';
import type { SagittaResult, UnitSystem } from './types';
import { PRECISION_LEVELS, UNIT_SUFFIX } from './types';
import { formatDrawingTolerance } from './clipboard';

const { Text, Title } = Typography;

function roundToDecimals(n: number, decimals: number): string {
	const f = 10 ** decimals;
	return (Math.round((n + Number.EPSILON) * f) / f).toFixed(decimals);
}

export interface ResultsPanelProps {
	unit: UnitSystem;
	result: SagittaResult | null;
	placeholder: string;
	onCopyAll: () => void;
	canCopy: boolean;
}

async function copyText(text: string) {
	try {
		await navigator.clipboard.writeText(text);
		message.success('Copied');
	} catch {
		message.error('Copy failed');
	}
}

function PrecisionColumn({
	label,
	decimals,
	result,
	unit,
	hasTol,
	drawingTolStr,
}: {
	label: string;
	decimals: number;
	result: SagittaResult;
	unit: UnitSystem;
	hasTol: boolean;
	drawingTolStr: string;
}) {
	const suf = UNIT_SUFFIX[unit];
	const nom = roundToDecimals(result.nominal, decimals);
	const u =
		result.upperLimit != null
			? roundToDecimals(result.upperLimit, decimals)
			: '—';
	const lo =
		result.lowerLimit != null
			? roundToDecimals(result.lowerLimit, decimals)
			: '—';
	const atp =
		result.tolPlus != null ? roundToDecimals(result.tolPlus, decimals) : '—';
	const atm =
		result.tolMinus != null ? roundToDecimals(result.tolMinus, decimals) : '—';
	const rowClass =
		'flex justify-between items-center py-1 border-b border-slate-100 last:border-0';
	return (
		<div className="rounded border border-slate-200 p-3 bg-slate-50/80 flex-1 min-w-0">
			<Text className="block text-xs font-medium text-slate-600 mb-2">
				{label}
			</Text>
			<div className={rowClass}>
				<Text strong className="text-base">
					Nominal Sagitta (s)
				</Text>
				<div className="flex items-center gap-1">
					<span className="text-2xl font-bold text-blue-800">{nom}</span>{' '}
					<Text type="secondary" className="text-xs">
						{suf}
					</Text>
					<Button
						type="text"
						size="small"
						icon={<CopyOutlined />}
						aria-label="Copy nominal"
						onClick={() => copyText(nom)}
					/>
				</div>
			</div>
			{hasTol ? (
				<>
					<div className={rowClass}>
						<Text>Upper Limit</Text>
						<div className="flex items-center gap-1">
							<span>
								{u} {suf}
							</span>
							{u !== '—' ? (
								<Button
									type="text"
									size="small"
									icon={<CopyOutlined />}
									onClick={() => copyText(String(u))}
								/>
							) : null}
						</div>
					</div>
					<div className={rowClass}>
						<Text>Lower Limit</Text>
						<div className="flex items-center gap-1">
							<span>
								{lo} {suf}
							</span>
							{lo !== '—' ? (
								<Button
									type="text"
									size="small"
									icon={<CopyOutlined />}
									onClick={() => copyText(String(lo))}
								/>
							) : null}
						</div>
					</div>
					<div className={rowClass}>
						<Text>Radius Adjusted Tolerance</Text>
						<div className="flex items-center gap-1">
							<span>
								+{atp} / −{atm} {suf}
							</span>
							<Button
								type="text"
								size="small"
								icon={<CopyOutlined />}
								onClick={() => copyText(`+${atp} / -${atm}`)}
							/>
						</div>
					</div>
					<div className={rowClass}>
						<Text>Drawing tolerance</Text>
						<div className="flex items-center gap-1 text-right">
							<Text className="!mb-0 max-w-[140px]">{drawingTolStr}</Text>
							<Button
								type="text"
								size="small"
								icon={<CopyOutlined />}
								onClick={() => copyText(drawingTolStr)}
							/>
						</div>
					</div>
				</>
			) : null}
		</div>
	);
}

function FullPrecisionRow({
	label,
	value,
	unit: suf,
}: {
	label: string;
	value: string;
	unit: string;
}) {
	return (
		<div className="flex justify-between items-center">
			<Text type="secondary" className="text-xs">
				{label}
			</Text>
			<div className="flex items-center gap-1 tabular-nums text-xs">
				<span className="font-mono">{value}</span>
				<Text type="secondary" className="text-[10px]">
					{suf}
				</Text>
				<Button
					type="text"
					size="small"
					icon={<CopyOutlined />}
					className="!w-5 !h-5 !min-w-0"
					onClick={() => copyText(value)}
				/>
			</div>
		</div>
	);
}

export default function ResultsPanel({
	unit,
	result,
	placeholder,
	onCopyAll,
	canCopy,
}: ResultsPanelProps) {
	if (!result || Number.isNaN(result.nominal)) {
		return (
			<div className="min-h-[120px] flex flex-col items-center justify-center gap-1 rounded border border-dashed border-slate-200 bg-slate-50/50 p-4 text-center">
				<CalculatorOutlined
					className="text-2xl text-slate-400"
					aria-hidden
				/>
				<Text type="secondary" className="text-sm">
					{placeholder}
				</Text>
				<Text type="secondary" className="text-xs">
					Sagitta updates automatically as you type.
				</Text>
			</div>
		);
	}

	const levels = PRECISION_LEVELS[unit];
	const hasTol = result.hasToleranceDisplay;
	const fullPrec = 6;
	const fullNom = result.nominal.toFixed(fullPrec);
	const fullUp =
		result.upperLimit != null ? result.upperLimit.toFixed(fullPrec) : null;
	const fullLo =
		result.lowerLimit != null ? result.lowerLimit.toFixed(fullPrec) : null;
	const fullAtp =
		result.tolPlus != null ? result.tolPlus.toFixed(fullPrec) : null;
	const fullAtm =
		result.tolMinus != null ? result.tolMinus.toFixed(fullPrec) : null;
	const suf = UNIT_SUFFIX[unit];
	const drawingTolStr = hasTol ? formatDrawingTolerance(result, unit) : '—';

	return (
		<div className="space-y-4">
			<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
				<Title level={5} className="!mb-0">
					Results
				</Title>
				<Button
					icon={<CopyOutlined />}
					onClick={onCopyAll}
					type="primary"
					size="small"
					disabled={!canCopy}
				>
					Copy All
				</Button>
			</div>

			<div className="flex flex-col md:flex-row gap-3 w-full">
				{levels.map((level) => (
					<PrecisionColumn
						key={level.label}
						label={level.label}
						decimals={level.decimals}
						result={result}
						unit={unit}
						hasTol={hasTol}
						drawingTolStr={drawingTolStr}
					/>
				))}
			</div>

			<div className="rounded border border-slate-200 bg-white p-3">
				<Text className="block text-xs font-medium text-slate-500 mb-2">
					Full Precision Reference
				</Text>
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
					<FullPrecisionRow
						label="Nominal Sagitta (s)"
						value={fullNom}
						unit={suf}
					/>
					{hasTol && fullUp && (
						<FullPrecisionRow label="Upper Limit" value={fullUp} unit={suf} />
					)}
					{hasTol && fullLo && (
						<FullPrecisionRow label="Lower Limit" value={fullLo} unit={suf} />
					)}
					{hasTol && fullAtp && fullAtm && (
						<FullPrecisionRow
							label="Radius Adjusted Tol."
							value={`+${fullAtp} / −${fullAtm}`}
							unit={suf}
						/>
					)}
					{hasTol && (
						<FullPrecisionRow
							label="Drawing Tolerance"
							value={drawingTolStr}
							unit=""
						/>
					)}
				</div>
			</div>
		</div>
	);
}

